import { useStressTestStore } from '../../store/stressTestStore';
import { useScenarioStore } from '../../store/scenarioStore';
import { useNetworkUIStore } from '../../store/networkUIStore';
import type { PlanningCoupling } from '../../types/planning';
import { LOADING_COMPLIANCE_LIMIT_PCT } from '../../utils/loadingConstants';

/**
 * The calculation unit the scenario is solved on: the zone and every zone it is
 * galvanically coupled to, plus every Trennstelle touching them. Toggling a
 * Trennstelle here changes the SCENARIO only (measure `coupling_switch`): the map
 * loads the joined zone at once, the run compares today's switch states with the
 * scenario's.
 */
export function UnitCard() {
  const unit = useStressTestStore(s => s.unit);
  const isLoadingUnit = useStressTestStore(s => s.isLoadingUnit);
  const stressZoneId = useStressTestStore(s => s.stressZoneId);
  const expandedZoneIds = useStressTestStore(s => s.expandedZoneIds);
  const loadingZoneIds = useStressTestStore(s => s.loadingZoneIds);
  const toggleZone = useStressTestStore(s => s.toggleZone);
  const run = useStressTestStore(s => s.run);
  const pendingMeasures = useScenarioStore(s => s.pendingMeasures);
  const setMeasure = useScenarioStore(s => s.setMeasure);
  const removeMeasure = useScenarioStore(s => s.removeMeasure);
  const setMapView = useNetworkUIStore(s => s.setMapView);

  if (!unit || !stressZoneId) return null;

  const toggle = (c: PlanningCoupling) => {
    const target = !c.isClosed;
    const measure = pendingMeasures.find(m => m.kind === 'coupling_switch' && m.elementId === c.id);
    if (target === c.isClosedLive) {
      if (measure) removeMeasure(measure.id);
      return;
    }
    setMeasure({ id: measure?.id ?? crypto.randomUUID(), kind: 'coupling_switch', elementId: c.id, isClosed: target });
  };

  const nameOf = (id: string) => unit.zoneNames[id] ?? id;
  const internal = unit.couplings.filter(c => unit.zoneIds.includes(c.zoneAId) && unit.zoneIds.includes(c.zoneBId));
  const external = unit.couplings.filter(c => !(unit.zoneIds.includes(c.zoneAId) && unit.zoneIds.includes(c.zoneBId)));

  const Row = ({ c }: { c: PlanningCoupling }) => {
    const overridden = c.isClosed !== c.isClosedLive;
    return (
      <li className="py-1 flex items-center gap-2" data-testid="unit-coupling-row">
        <button
          type="button"
          onClick={() => { if (c.lat != null && c.lng != null) setMapView({ lat: c.lat, lng: c.lng }, 18); }}
          className="flex-1 min-w-0 text-left"
          title={c.name}
        >
          <span className="block text-xs text-gray-800 dark:text-primary-100 truncate">
            {c.zoneAName ?? nameOf(c.zoneAId)} ↔ {c.zoneBName ?? nameOf(c.zoneBId)}
          </span>
          <span className="block text-[10px] text-gray-500 dark:text-primary-400 truncate">
            {c.switchElementType === 'trafo' ? 'ONS' : 'KVS'} {c.switchElementName ?? c.switchElementId}{c.cableName ? ` · ${c.cableName}` : ''}
          </span>
        </button>
        <button
          type="button"
          onClick={() => toggle(c)}
          className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${c.isClosed ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'} ${overridden ? 'ring-2 ring-amber-400' : ''}`}
          title={overridden ? `Im Szenario ${c.isClosed ? 'geschlossen' : 'offen'} (heute ${c.isClosedLive ? 'geschlossen' : 'offen'}) — klicken, um zurückzusetzen` : `Heute ${c.isClosed ? 'geschlossen' : 'offen'} — klicken, um im Szenario ${c.isClosed ? 'zu öffnen' : 'zu schließen'}`}
        >
          {c.isClosed ? 'geschlossen' : 'offen'}{overridden ? ' *' : ''}
        </button>
      </li>
    );
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-700 overflow-hidden" data-testid="unit-card">
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-800 dark:text-primary-100">Netzverbund</h3>
          <span className="text-[10px] text-gray-500 dark:text-primary-400">{isLoadingUnit ? 'lädt …' : `${unit.zoneIds.length} ${unit.zoneIds.length === 1 ? 'Zone' : 'Zonen'} · ${unit.couplingCount} geschlossen`}</span>
        </div>
        <p className="text-[11px] text-gray-600 dark:text-primary-300 leading-snug mb-1.5">
          {unit.isMulti
            ? <>Gerechnet wird das zusammenhängende Netz. Nachbarzonen liegen als Blasen auf der Karte — anklicken oder hier dazuschalten, um ihre Elemente zu sehen (mehrere gleichzeitig möglich).</>
            : <>Die Zone hängt derzeit an keiner geschlossenen Trennstelle — sie wird allein gerechnet.</>}
          {unit.couplingOverrides > 0 && <> <span className="text-amber-700 dark:text-amber-300">{unit.couplingOverrides} Trennstelle{unit.couplingOverrides > 1 ? 'n' : ''} im Szenario umgeschaltet (*).</span></>}
        </p>
        {unit.isMulti && (
          <ul className="divide-y divide-gray-100 dark:divide-primary-600 mb-1" data-testid="unit-zone-list">
            {unit.zoneIds.map(z => {
              const isTarget = z === stressZoneId;
              const expanded = isTarget || expandedZoneIds.includes(z);
              const loading = loadingZoneIds.includes(z);
              const zs = run?.zoneSummaries?.[z];
              return (
                <li key={z} className="py-1 flex items-center gap-2" data-testid="unit-zone-row">
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs text-gray-800 dark:text-primary-100 truncate">{nameOf(z)}{isTarget ? ' (gewählt)' : ''}</span>
                    {zs && (
                      <span className={`block text-[10px] tabular-nums ${zs.maxLoadingWithPct > LOADING_COMPLIANCE_LIMIT_PCT ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-primary-400'}`}>
                        max. {zs.maxLoadingWithoutPct.toLocaleString('de-DE', { maximumFractionDigits: 0 })} → {zs.maxLoadingWithPct.toLocaleString('de-DE', { maximumFractionDigits: 0 })} %{zs.overloadsWith > 0 ? ` · ${zs.overloadsWith} überlastet` : ''}
                      </span>
                    )}
                  </span>
                  {!isTarget && (
                    <button
                      type="button"
                      onClick={() => void toggleZone(z)}
                      disabled={loading}
                      className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium disabled:opacity-50 ${expanded ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-gray-100 text-gray-700 dark:bg-primary-600 dark:text-primary-200'}`}
                      data-testid="zone-toggle"
                    >
                      {loading ? 'lädt …' : expanded ? 'ausblenden' : 'dazuschalten'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {internal.length > 0 && (
          <>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-primary-400 mt-1">Im Verbund</p>
            <ul className="divide-y divide-gray-100 dark:divide-primary-600">{internal.map(c => <Row key={c.id} c={c} />)}</ul>
          </>
        )}
        {external.length > 0 && (
          <>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-primary-400 mt-1">Zu Nachbarzonen (offen)</p>
            <ul className="divide-y divide-gray-100 dark:divide-primary-600">{external.map(c => <Row key={c.id} c={c} />)}</ul>
          </>
        )}
        {unit.couplings.length === 0 && (
          <p className="text-[11px] text-gray-500 dark:text-primary-400">Keine Trennstellen zu Nachbarzonen erfasst.</p>
        )}
        <p className="text-[10px] text-gray-500 dark:text-primary-400 mt-1.5 leading-snug">
          Umschalten wirkt nur im Szenario: Schließen holt die Nachbarzone in die Rechnung, Öffnen trennt sie. Der Vergleichslauf rechnet den heutigen Schaltzustand.
        </p>
      </div>
    </div>
  );
}
